<template>
  <div class="code-input-container">
    <form @submit.prevent>
      <div class="flex justify-center mb-4">
        <input
          v-for="(digit, index) in length"
          :key="index"
          :ref="`input${index}`"
          v-model="digits[index]"
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="w-10 h-12 text-center text-xl border-2 rounded-xl focus:outline-none m-1"
          :class="{ 'border-red-600 focus:border-red-700': status === 'unauthorized', 'border-gray-400 focus:border-gray-400': status !== 'unauthorized' }"
          @input="handleInput(index, $event)"
          @keydown="handleKeydown(index, $event)"
          @paste="handlePaste"
        />
      </div>
      
      <!-- Status message -->
      <div v-if="status === 'unauthorized'" class="text-red-500 text-center mb-4">
        Invalid access code. Please try again.
      </div>
      
      <br/>
      <!-- Slot for additional content -->
      <slot></slot>
    </form>
  </div>
</template>

<script>
export default {
  name: 'CodeInput',
  props: {
    length: {
      type: Number,
      default: 4
    },
    status: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      digits: Array(this.length).fill('')
    }
  },
  watch: {
    status(newStatus) {
      if (newStatus === 'unauthorized') {
        // Clear the input on unauthorized
        this.digits = Array(this.length).fill('')
        this.$nextTick(() => {
          this.focusInput(0)
        })
      }
    },
    digits: {
      handler(newDigits) {
        // Reset status when input is cleared
        if (newDigits.every(digit => digit === '') && this.status) {
          this.$emit('update:status', null)
        }
      },
      deep: true
    }
  },
  mounted() {
    this.focusInput(0)
  },
  methods: {
    handleInput(index, event) {
      const value = event.target.value
      
      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        this.digits[index] = ''
        return
      }
      
      this.digits[index] = value
      
      // Move to next input if value entered
      if (value && index < this.length - 1) {
        this.focusInput(index + 1)
      }
      
      // Check if all digits are filled
      if (this.digits.every(digit => digit !== '')) {
        const code = this.digits.join('')
        this.$emit('submit', code)
      }
    },
    
    handleKeydown(index, event) {
      const inputElement = event.target
      const currentValue = inputElement.value
      
      // Check if text is selected
      const hasSelection = inputElement.selectionStart !== inputElement.selectionEnd
      
      if (!hasSelection) {
        // Allow only numbers and some control keys
        if (!event.key.match(/[0-9]/) && !event.ctrlKey && event.key.length === 1) {
          event.preventDefault()
        }
        
        // Handle backspace
        if (event.key === 'Backspace' && currentValue.length === 0 && index > 0) {
          this.focusInput(index - 1)
        } else if (event.key === 'ArrowLeft' && index > 0) {
          this.focusInput(index - 1)
        } else if (event.key === 'ArrowRight' && index < this.length - 1) {
          this.focusInput(index + 1)
        } else if (event.key !== 'Backspace' && !event.ctrlKey && currentValue.length >= 1 && index < this.length - 1) {
          // When typing a second character, focus already on the next input field
          this.focusInput(index + 1)
        } else if (currentValue.length >= 1) {
          // Stop adding new character at the last input field when already filled
          if (event.key.match(/[a-zA-Z0-9]/) && !event.ctrlKey && event.key.length === 1) {
            event.preventDefault()
          }
        }
      }
    },
    
    handlePaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      const numbers = pastedData.replace(/\D/g, '').slice(0, this.length)
      
      // Fill the inputs with pasted numbers
      for (let i = 0; i < this.length; i++) {
        this.digits[i] = numbers[i] || ''
      }
      
      // Focus the next empty input or submit if all filled
      const nextEmptyIndex = this.digits.findIndex(digit => digit === '')
      if (nextEmptyIndex !== -1) {
        this.focusInput(nextEmptyIndex)
      } else {
        const code = this.digits.join('')
        this.$emit('submit', code)
      }
    },
    
    focusInput(index) {
      this.$nextTick(() => {
        const input = this.$refs[`input${index}`]
        if (input && input[0]) {
          input[0].focus()
        }
      })
    }
  }
}
</script>

<style scoped>
.code-input-container {
  width: 100%;
}
</style> 